import { Head } from "@inertiajs/react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { focusRing } from "@/components/ui/buttonStyles";
import { cn } from "@/utils/cn";

const CC_BY_SA = "https://creativecommons.org/licenses/by-sa/4.0/";

function SourceLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      className={cn(
        "rounded-sm text-em underline decoration-border-divider underline-offset-4",
        "transition-colors duration-200 hover:decoration-current",
        focusRing,
      )}
    >
      {children}
    </a>
  );
}

export default function About() {
  return (
    <PublicLayout>
        <Head>
          <title>About — Learn Languages Through Reading</title>
          <meta
            name="description"
            content="Mimir Languages is built on a simple belief: the best way to learn a language is through reading. No gamification, no pressure — just depth, context, and patience."
          />
          <link rel="canonical" href="https://mimirlang.eu/about" />
          <meta property="og:title" content="About — Mimir Languages" />
          <meta
            property="og:description"
            content="The best way to learn a language is through reading. No gamification, no pressure — just depth, context, and patience."
          />
          <meta property="og:url" content="https://mimirlang.eu/about" />
          <meta property="og:type" content="website" />
        </Head>

          <article className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
            <p className="font-mono text-xs uppercase tracking-widest text-muted mb-6 sm:mb-8">
              About Mímir
            </p>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight text-heading">
              Language, learned through{" "}
              <em className="italic text-em">living text</em>.
            </h1>

            <p className="mt-10 font-serif text-lg leading-relaxed text-muted">
              Mímir is built on a simple idea. The best way to learn a language
              is to read in it. No pressure. No streaks. No levels to grind.
              Just real text, and the time to sit with it.
            </p>

            <div className="mt-12 space-y-6 border-l border-accent pl-6 font-serif text-base leading-relaxed text-body">
              <p>
                Every word you meet here lives inside a sentence, a paragraph, a
                story. That is not an afterthought. That is the method. When you
                learn a word in context, you take in more than its meaning. You
                take in its rhythm, the company it keeps, the way it feels on
                the page.
              </p>
              <p>
                There are no timers. No leaderboards. No badges. Progress is
                quiet, and real. The only thing that matters is whether you
                understand a little more today than you did yesterday.
              </p>
            </div>

            <section className="mt-16">
              <h2 className="font-display text-2xl text-heading">The name</h2>
              <div className="mt-8 space-y-6 border-l border-border-divider pl-6 font-serif text-base leading-relaxed text-body">
                <p>
                  In Norse mythology, Mímir is the wisest of all beings. He
                  guards the well of knowledge beneath the world tree. Odin gave
                  up an eye for a single drink from that well, because wisdom is
                  never free.
                </p>
                <p>
                  We chose the name as a quiet reminder. Real understanding asks
                  for patience, and a willingness to go slowly. There are no
                  shortcuts to fluency. Only the steady work of reading, sitting
                  with the text, and coming back to it tomorrow.
                </p>
              </div>
            </section>

            <section className="mt-16">
              <h2 className="font-display text-2xl text-heading">Sources</h2>
              <div className="mt-8 space-y-6 border-l border-border-divider pl-6 font-serif text-base leading-relaxed text-body">
                <p>
                  Definitions and translations are adapted from{" "}
                  <SourceLink href="https://en.wiktionary.org">Wiktionary</SourceLink>,
                  shared under <SourceLink href={CC_BY_SA}>CC BY-SA 4.0</SourceLink>.
                </p>
                <p>
                  Icelandic inflections come from{" "}
                  <SourceLink href="https://bin.arnastofnun.is">
                    Beygingarlýsing íslensks nútímamáls
                  </SourceLink>
                  , © The Árni Magnússon Institute for Icelandic Studies
                  2002–{new Date().getFullYear()}, shared under{" "}
                  <SourceLink href={CC_BY_SA}>CC BY-SA 4.0</SourceLink>.
                </p>
              </div>
            </section>

            <div
              aria-hidden="true"
              className="mt-16 text-center font-display text-2xl text-accent"
            >
              ᛗ
            </div>
          </article>
    </PublicLayout>
  );
}
